import React from 'react';
import i18n from 'i18next';
import { createLogger } from '../../utils/logger';
import './error-pages.scss';

const logger = createLogger('AppErrorBoundary');

interface State {
  hasError: boolean;
  errorMessage: string;
}

interface Props {
  children?: React.ReactNode;
}

/**
 * Top-level error boundary that catches any unhandled React rendering errors.
 * Shows a branded 500-style fallback instead of a blank or raw error screen.
 */
class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true, errorMessage: 'An unexpected error occurred.' };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log for monitoring; do NOT surface details to the user
    logger.error('Uncaught error:', error, info.componentStack);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, errorMessage: '' });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-page">
        <div className="error-page__card">
          <div className="error-page__brand">RealityCoach</div>

          <div className="error-page__code error-page__code--red">500</div>

          <h1 className="error-page__title">{i18n.t('errorPages.somethingWentWrong')}</h1>

          <p className="error-page__description">
            {i18n.t('errorPages.appErrorDescription')}
          </p>

          <div className="error-page__actions">
            <button className="error-page__btn-primary" onClick={() => window.location.reload()}>
              {i18n.t('common.refreshPage')}
            </button>
            <button className="error-page__btn-secondary" onClick={this.handleRetry}>
              {i18n.t('errorPages.tryAgain')}
            </button>
          </div>

          <p className="error-page__footer">{i18n.t('errorPages.appErrorCode')}</p>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
